import React, { useMemo, useState } from "react";
import { shallowEqual, useSelector } from "react-redux";
import { useLocation } from "react-router-dom";
import { useIntl } from "react-intl";
import moment from "moment";
import { Button, Input, Typography } from "antd";
import { clientName, formatAddress, money } from "./format";

const { Text } = Typography;
const { TextArea } = Input;

const cell = { border: "1px solid #d9d9d9", padding: "2px 4px", whiteSpace: "nowrap", verticalAlign: "top" };
const head = { ...cell, textAlign: "left", fontWeight: "bold" };

const compactDimensions = (carpet) => {
  if (!carpet) return "";
  const length = carpet.dimensions ? carpet.dimensions.length : carpet.length;
  const width = carpet.dimensions ? carpet.dimensions.width : carpet.width;
  if (length == null && width == null) return "";
  return `${width || "–"}×${length || "–"}`;
};

export default function Report() {
  const intl = useIntl();
  const config = useSelector((state) => state.config, shallowEqual);
  const location = useLocation();
  const entries = (location.state && location.state.entries) || [];
  const sheet = (location.state && location.state.sheet) || (location.state && location.state.showAllColumns ? "checkOut" : "entries");
  const [notes, setNotes] = useState("");
  const [printedAt, setPrintedAt] = useState(() => new Date());

  const groups = useMemo(() => {
    const byClient = new Map();
    entries.forEach((entry) => {
      const key = entry.client ? entry.client._id : "none";
      const list = byClient.get(key) || [];
      list.push(entry);
      byClient.set(key, list);
    });
    return Array.from(byClient.values());
  }, [entries]);

  const priceHalf = { width: 64, minWidth: 64, minHeight: 18, padding: "2px 4px", boxSizing: "border-box" };
  const priceCell = {
    id: "price",
    label: intl.formatMessage({ id: "price" }),
    cellStyle: { width: 128, minWidth: 128 },
    headerStyle: { textAlign: "center" },
    render: (entry) => (
      <div style={{ display: "flex", margin: "-2px -4px" }}>
        <div style={{ ...priceHalf, borderRight: "1px solid #d9d9d9", textDecoration: entry.jobPrice != null && entry.jobPrice !== "" ? "line-through" : undefined }}>{money((entry.carpet || {}).price) || "\u00a0"}</div>
        <div style={priceHalf}>{money(entry.jobPrice) || "\u00a0"}</div>
      </div>
    ),
  };
  const clientColumn = {
    id: "client",
    label: intl.formatMessage({ id: "client" }),
    wrap: true,
    bold: true,
    render: (entry) => {
      const client = entry.client;
      return [
        clientName(client),
        client && client.phone,
        client && client.address && formatAddress(client.address),
      ].filter(Boolean).join(" · ");
    },
  };
  const categoryColumn = {
    id: "category",
    label: intl.formatMessage({ id: "category" }),
    render: (entry) => (entry.carpet && entry.carpet.category ? intl.formatMessage({ id: entry.carpet.category }) : ""),
  };
  const columns = (sheet === "checkIn" ? [
    clientColumn,
    { id: "ref", label: intl.formatMessage({ id: "ref" }), render: (entry) => (entry.carpet || {})._ref },
    categoryColumn,
    { id: "description", label: intl.formatMessage({ id: "description" }), wrap: true, render: (entry) => (entry.carpet || {}).description || "" },
    { id: "length", label: intl.formatMessage({ id: "widthXLength" }), render: (entry) => compactDimensions(entry.carpet) },
    { id: "storageBox", label: intl.formatMessage({ id: "storageBox" }), render: (entry) => entry.storageBox || "" },
  ] : sheet === "checkOut" ? [
    clientColumn,
    { id: "ref", label: intl.formatMessage({ id: "ref" }), render: (entry) => (entry.carpet || {})._ref },
    priceCell,
    { id: "length", label: intl.formatMessage({ id: "widthXLength" }), render: (entry) => compactDimensions(entry.carpet) },
    {
      id: "cleanStatus",
      label: intl.formatMessage({ id: "cleanStatusShort" }),
      render: (entry) => intl.formatMessage({ id: entry.cleaned ? "yes" : "no" }),
    },
    { id: "inAt", label: intl.formatMessage({ id: "inAt" }), render: (entry) => moment(entry.inAt).format("DD/MM HH:mm") },
  ] : [
    clientColumn,
    { id: "ref", label: intl.formatMessage({ id: "ref" }), render: (entry) => (entry.carpet || {})._ref },
    categoryColumn,
    { id: "length", label: intl.formatMessage({ id: "widthXLength" }), render: (entry) => compactDimensions(entry.carpet) },
    {
      id: "cleanStatus",
      label: intl.formatMessage({ id: "cleanStatusShort" }),
      render: (entry) => intl.formatMessage({ id: entry.cleaned ? "yes" : "no" }),
    },
    priceCell,
    { id: "inAt", label: intl.formatMessage({ id: "inAt" }), render: (entry) => moment(entry.inAt).format("DD/MM HH:mm") },
    {
      id: "outAt",
      label: intl.formatMessage({ id: "outAt" }),
      render: (entry) => (entry.outAt ? moment(entry.outAt).format("DD/MM HH:mm") : ""),
    },
  ]);

  if (!entries.length) return <Text>{intl.formatMessage({ id: "entries" })}</Text>;

  return (
    <div>
      <div className="no-print" style={{ marginBottom: 12 }}>
        <Button type="primary" onClick={() => { setPrintedAt(new Date()); setTimeout(() => window.print(), 0); }}>{intl.formatMessage({ id: "print" })}</Button>
      </div>
      <div className="no-print" style={{ marginBottom: 12, fontSize: 13 }}>
        <div style={{ fontWeight: 600, marginBottom: 4 }}>{intl.formatMessage({ id: "generalNotes" })}</div>
        <TextArea autoSize value={notes} onChange={(event) => setNotes(event.target.value)} />
      </div>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline", fontWeight: "bold", fontSize: 14, marginBottom: 4 }}>
        <div>{(config && config.companyName) || "D.N.A Υπηρεσίες Καθαρισμού"}</div>
        <div>{intl.formatMessage({ id: sheet === "checkIn" ? "checkIn" : sheet === "checkOut" ? "checkOut" : "entries" })} {moment(printedAt).format("DD/MM/YYYY HH:mm")}</div>
      </div>
      <div style={{ fontSize: 11, marginBottom: 6 }}>
        <strong>{intl.formatMessage({ id: "generalNotes" })}:</strong> {notes}
      </div>
      <table className="carpet-report" style={{ width: "100%", borderCollapse: "collapse", fontSize: 11 }}>
        <thead>
          <tr>
            {columns.map((column) => (
              <th key={column.id} style={{ ...head, ...column.cellStyle, ...column.headerStyle }}>{column.label}</th>
            ))}
          </tr>
        </thead>
        <tbody>
          {groups.map((entriesForClient) => {
            const client = entriesForClient[0].client;
            const clientKey = client ? client._id : "none";
            return (
              <React.Fragment key={clientKey}>
                {entriesForClient.map((entry, index) => {
                  const noteParts = [
                    entry.inNotes && entry.inNotes.trim() && { id: "inNotes", text: entry.inNotes.trim() },
                    entry.outNotes && entry.outNotes.trim() && { id: "outNotes", text: entry.outNotes.trim() },
                  ].filter(Boolean);
                  return (
                    <React.Fragment key={entry._id}>
                      <tr>
                        {columns.map((column) => (
                          <td key={column.id} style={{ ...(column.wrap ? { ...cell, whiteSpace: "normal" } : cell), ...(column.bold ? { fontWeight: "bold" } : {}), ...(column.id === "client" && index > 0 ? { textAlign: "center" } : {}), ...column.cellStyle }}>
                            {column.id === "client" && index > 0 ? "↑" : column.render(entry)}
                          </td>
                        ))}
                      </tr>
                      {noteParts.length > 0 && (
                        <tr>
                          <td colSpan={columns.length} style={{ ...cell, padding: 0 }}>
                            <div style={{ display: "flex" }}>
                              {noteParts.map((part, partIndex) => (
                                <div
                                  key={part.id}
                                  style={{
                                    width: noteParts.length === 1 ? "100%" : "50%",
                                    minHeight: 18,
                                    padding: "2px 4px",
                                    borderRight: partIndex === 0 && noteParts.length > 1 ? "1px solid #d9d9d9" : undefined,
                                    whiteSpace: "normal",
                                  }}
                                >
                                  {intl.formatMessage({ id: part.id })}: {part.text}
                                </div>
                              ))}
                            </div>
                          </td>
                        </tr>
                      )}
                    </React.Fragment>
                  );
                })}
              </React.Fragment>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}
